import { FancySharePublicDto } from '@diritto/common/fancyShare/dtos/fancy-share.dtos';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfilePublicRelayDto } from '@diritto/common/relay/dtos/profile-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import styled from '@emotion/styled';
import { FancyShareMoreMenu } from 'app-components/fancyShare/FancyShareMoreMenu';
import { FancyShareAction } from 'constants/fancyShare';
import { useAuthState } from 'contexts/AuthProvider';
import { FavoriteEmptyIcon, FavoriteFillIcon } from 'icons/FavoriteIcon';
import { ReplyIcon } from 'icons/ReplyIcon';
import { useRouter } from 'next/navigation';
import React, { useCallback, useMemo } from 'react';
import { FancyShareDispatchParams } from 'state-hooks/fancyShare/useFancyShareDispatch';
import { useFancyShareLike } from 'state-hooks/fancyShare/useFancyShareLike';
import { IconButton } from 'ui-components/common/IconButton';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { showAlertModal } from 'utils/modal';

interface FancyShareReactionProps {
  fancyShare: FancySharePublicDto;
  isLiked: boolean;
  actions?: FancyShareAction[];
  replyCount?: number;
  onDispatchAction: (params: FancyShareDispatchParams) => void;
  showMenuButton: boolean;
  isShowReplyWrite?: boolean;
  isShowReplyCount?: boolean;
  content?: ContentPublicRelayDto;
  authorProfile?: ProfilePublicRelayDto;
}

export const FancyShareReaction = ({
  fancyShare,
  actions,
  showMenuButton,
  replyCount = 0,
  isLiked,
  onDispatchAction,
  content,
  authorProfile,
  isShowReplyWrite = false,
  isShowReplyCount = false,
}: FancyShareReactionProps) => {
  const router = useRouter();
  const { isLoggedIn, user } = useAuthState();
  const requestSign = useRequestSign();
  const requestCertificate = useRequestCertificate();
  const { liked, likeCount, toggleLike } = useFancyShareLike({
    fancyShareId: fancyShare.id,
    isLiked,
    likeCount: fancyShare.likeCount,
  });
  const isShowReply = (isShowReplyCount && replyCount > 0) || isShowReplyWrite;

  const replyText = useMemo(() => {
    let defaultText = '';
    if (isShowReplyWrite) {
      defaultText = '댓글 쓰기';
    }
    if (isShowReplyCount) {
      defaultText = replyCount > 0 ? `${replyCount}` : defaultText;
    }
    return defaultText;
  }, [isShowReplyWrite, isShowReplyCount, replyCount]);

  const handleClickLike = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isLoggedIn) {
        requestSign();
        return;
      }
      toggleLike();
    },
    [isLoggedIn, requestSign, toggleLike],
  );

  const handleClickReply = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      if (replyCount === 0) {
        if (!isLoggedIn) {
          requestSign();
          return;
        }

        if (!user?.ageVerified) {
          showAlertModal(
            { title: '본인인증을 해주세요.', description: '해당 기능을 사용하려면 본인인증이 필요해요.' },
            {
              onConfirm: requestCertificate,
              confirmText: '본인인증하러 가기',
              cancelText: '닫기',
            },
          );
          return;
        }
      }

      const fancyShareDetailUrl = `/fancy-shares/${fancyShare.id}?isInputFocus=${replyCount === 0}`;
      router.push(fancyShareDetailUrl);
    },
    [replyCount, router, fancyShare.id, isLoggedIn, user?.ageVerified, requestSign, requestCertificate],
  );

  return (
    <StyledBottom>
      <StyledIconButton onClick={handleClickLike}>
        <StyledIconWrapper>
          {liked ? <FavoriteFillIcon color={Color.Primary500} /> : <FavoriteEmptyIcon color={Color.Gray500} />}
        </StyledIconWrapper>
        <StyledText>{likeCount > 0 ? likeCount : '좋아요'}</StyledText>
      </StyledIconButton>
      {isShowReply && (
        <StyledIconButton onClick={handleClickReply}>
          <StyledIconWrapper>
            <ReplyIcon className="size-[16px]" color={Color.Gray500} />
          </StyledIconWrapper>
          <StyledText>{replyText}</StyledText>
        </StyledIconButton>
      )}
      {showMenuButton && actions && actions.length > 0 && (
        <FancyShareMoreMenu
          actions={actions}
          fancyShare={fancyShare}
          onDispatchAction={onDispatchAction}
          content={content}
          authorProfile={authorProfile}
        />
      )}
    </StyledBottom>
  );
};

const StyledIconButton = styled(IconButton)``;

const StyledBottom = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
`;

const StyledIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const StyledText = styled.p`
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
`;
